import { zodResolver } from "@hookform/resolvers/zod";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SubmitHandler, useForm } from "react-hook-form";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import { RobotoButton } from "@/shared/components/RobotoButton";
import {
  renameLayoutFormSchema,
  type RenameLayoutFormFields,
} from "@/shared/domain/layouts/LayoutFormModel";
import { LayoutRecord } from "@/shared/domain/layouts/LayoutRecord";
import { PaginatedResponse } from "@/shared/http";
import { RobotoDomainException } from "@/shared/http/exceptions";
import { LoggerService } from "@/shared/services";

interface RenameLayoutModalProps {
  layout: LayoutRecord | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (layout: LayoutRecord) => void;
}

export function RenameLayoutModal({
  layout,
  isOpen,
  onClose,
  onSuccess,
}: RenameLayoutModalProps) {
  const form = useForm<RenameLayoutFormFields>({
    defaultValues: {
      name: "",
    },
    values: {
      name: layout?.name ?? "",
    },
    mode: "onBlur",
    resolver: zodResolver(renameLayoutFormSchema),
  });

  const renameLayout = useRenameLayout();

  const serverError = form.formState.errors.root?.serverErrors?.message;

  const onSubmit: SubmitHandler<RenameLayoutFormFields> = (data) => {
    if (!layout) {
      return;
    }

    return renameLayout
      .mutateAsync({
        layoutId: layout.layout_id,
        updates: { name: data.name },
      })
      .then(onSuccess);
  };

  if (!layout) {
    return null;
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby="rename-layout-dialog-title"
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
    >
      <form
        onSubmit={(event) => {
          form
            .handleSubmit(onSubmit)(event)
            .catch((e) => {
              LoggerService.error("Failed to rename layout:", e);

              let errorMessage = "Failed to rename layout. Please try again.";

              // If it's a client error, we can show the user the message
              if (e instanceof RobotoDomainException && e.isClientError()) {
                errorMessage = e.message;
              }

              form.setError("root.serverErrors", {
                message: errorMessage,
              });
            });
        }}
      >
        <DialogTitle id="rename-layout-dialog-title" sx={{ pr: 6 }}>
          Rename layout
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            <Typography variant="body1">
              Enter a new name for <b>&quot;{layout.name}&quot;</b>:
            </Typography>

            <TextField
              {...form.register("name")}
              autoFocus
              disabled={renameLayout.isPending}
              error={!!form.formState.errors.name}
              fullWidth
              helperText={form.formState.errors.name?.message}
              label="Layout name"
              variant="outlined"
            />
            {serverError && <Alert severity="error">{serverError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <RobotoButton
            eventName="RenameLayoutCanceled"
            eventProperties={{
              layoutId: layout.layout_id,
              layoutName: layout.name,
            }}
            onClick={onClose}
            type="button"
            disabled={renameLayout.isPending}
          >
            Cancel
          </RobotoButton>
          <RobotoButton
            eventName="RenameLayoutClicked"
            eventProperties={{
              layoutId: layout.layout_id,
              layoutName: layout.name,
              newName: form.watch("name"),
            }}
            type="submit"
            variant="contained"
            disabled={renameLayout.isPending || !form.formState.isValid}
            endIcon={
              renameLayout.isPending ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            Rename Layout
          </RobotoButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function useRenameLayout() {
  const { layoutService } = useDomainServices();
  const { currentOrganization } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      layoutId: string;
      updates: { name: string };
    }) => {
      if (currentOrganization === null) {
        return Promise.reject(new Error("CurrentOrganization is not set"));
      }

      return layoutService.updateLayout(
        request.layoutId,
        request.updates,
        currentOrganization.org_id,
      );
    },
    onSuccess: async (layoutRecord: LayoutRecord) => {
      await queryClient.setQueryData(
        ["layout", layoutRecord.layout_id],
        layoutRecord,
      );
      return queryClient.setQueryData<PaginatedResponse<LayoutRecord>>(
        ["layouts"],
        (oldData) => {
          if (!oldData) {
            return undefined;
          }
          return {
            ...oldData,
            items: oldData.items.map((record) => {
              if (record.layout_id === layoutRecord.layout_id) {
                return layoutRecord;
              }
              return record;
            }),
          };
        },
      );
    },
  });
}
